import { useEffect, useMemo } from 'react';

import { useCustomNameGoalsCompanyTabs } from 'Deer/hooks/useCustomNameGoalsCompanyTabs';
import { useRouteToMyTeamLevels, useRouteToNavLevels } from 'Deer/hooks/useRouteToL3';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useDispatch } from 'react-redux';
import { setSelectedView } from 'reducers/viewsSlice';
import Header from 'screens/Team/Rabbit/Goals/Header';
import { checkIntentLandingPagesFeatureEnabled } from 'utils/helpers/featureFlags';

import { modifyString, StringModifier } from 'constants/copyHelpers';
import {
  sendBulkUploadDownloadExtendedGoalSampleFileEventCompany,
  sendBulkUploadDownloadExtendedGoalSampleFileEventDirectTeam,
  sendBulkUploadDownloadExtendedGoalSampleFileEventMe,
  sendBulkUploadDownloadExtendedGoalSampleFileEventSkipTeam,
  sendBulkUploadDownloadQuickGoalSampleFileEventCompany,
  sendBulkUploadDownloadQuickGoalSampleFileEventDirectTeam,
  sendBulkUploadDownloadQuickGoalSampleFileEventMe,
  sendBulkUploadDownloadQuickGoalSampleFileEventSkipTeam,
  sendBulkUploadFileAttachedEventCompany,
  sendBulkUploadFileAttachedEventDirectTeam,
  sendBulkUploadFileAttachedEventMe,
  sendBulkUploadFileAttachedEventSkipTeam,
  sendBulkUploadFileUploadedEventCompany,
  sendBulkUploadFileUploadedEventDirectTeam,
  sendBulkUploadFileUploadedEventMe,
  sendBulkUploadFileUploadedEventSkipTeam,
  sendBulkUploadTimeLineChangeEventCompany,
  sendBulkUploadTimeLineChangeEventDirectTeam,
  sendBulkUploadTimeLineChangeEventMe,
  sendBulkUploadTimeLineChangeEventSkipTeam,
  sendBulkUploadViewGuideEventCompany,
  sendBulkUploadViewGuideEventDirectTeam,
  sendBulkUploadViewGuideEventMe,
  sendBulkUploadViewGuideEventSkipTeam,
  sendCompanyBulkUploadPageViewEvent,
  sendDirectTeamBulkUploadPageViewEvent,
  sendMeBulkUploadPageViewEvent,
  sendSkipTeamBulkUploadPageViewEvent,
} from 'constants/mixpanelHelper';

import { PageTabbedNavigationV1 } from 'uiToolkitV1/PageTabbedNavigationV1';

import { Layout } from '../components/Layout/Layout';
import { BulkUploadScreen } from './components/BulkUploadScreen';
import { IntentDropdown } from './components/IntentDropdown';

const COHORT_ID_NAME_MAP = {
  '-12': 'Me',
  '-2': 'Team',
  '-3': 'Team',
  '-1': 'Company',
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const UploadGoalsIntentLandingPage = ({ cohort, headerLabel }: { cohort: any; headerLabel: string }) => {
  const dispatch = useDispatch();
  const { moduleName } = useOrganisationSettings();

  const { l3Options, l1Module, teamType } = useRouteToNavLevels();

  const goalModuleNameTabs = useMemo(
    () =>
      l3Options.map((tab) => ({
        ...tab,
        label: `${tab.label} ${modifyString(moduleName, StringModifier.PLURAL_SENTENCE_CASE)}`,
      })),
    [l3Options, moduleName]
  );

  const isMyDataRoute = l1Module === 'myData';
  const isTeamRoute = l1Module === 'team';
  const isCompanyRoute = l1Module === 'company';

  const modifiedHeaderLabel = `${headerLabel} ${modifyString(moduleName, StringModifier.PLURAL_SENTENCE_CASE)}`;

  const { l3Options: teamTabs } = useRouteToMyTeamLevels();

  const { tabs: companyTabs } = useCustomNameGoalsCompanyTabs();

  let tabs = [];

  if (isMyDataRoute) {
    tabs = goalModuleNameTabs;
  } else if (isTeamRoute) {
    tabs = teamTabs;
  } else if (isCompanyRoute) {
    tabs = companyTabs;
  }
  useEffect(() => {
    if (isMyDataRoute) {
      sendMeBulkUploadPageViewEvent();
    } else if (isTeamRoute) {
      if (teamType === 'directReports') {
        sendDirectTeamBulkUploadPageViewEvent();
      } else if (teamType === 'allReports') {
        sendSkipTeamBulkUploadPageViewEvent();
      }
    } else if (isCompanyRoute) {
      sendCompanyBulkUploadPageViewEvent();
    }
  }, [isCompanyRoute, isMyDataRoute, isTeamRoute, teamType]);

  useEffect(() => {
    dispatch(setSelectedView({ ...cohort, teamId: cohort.id }));
    return () => {
      dispatch(setSelectedView({}));
    };
  }, [cohort, dispatch]);

  const handleTimePeriodChange = (callback: () => void) => {
    if (isMyDataRoute) {
      sendBulkUploadTimeLineChangeEventMe();
    } else if (isTeamRoute) {
      if (teamType === 'directReports') {
        sendBulkUploadTimeLineChangeEventDirectTeam();
      } else if (teamType === 'allReports') {
        sendBulkUploadTimeLineChangeEventSkipTeam();
      }
    } else if (isCompanyRoute) {
      sendBulkUploadTimeLineChangeEventCompany();
    }

    callback();
  };

  const handleFileAttached = () => {
    if (isMyDataRoute) {
      sendBulkUploadFileAttachedEventMe();
    } else if (isTeamRoute) {
      if (teamType === 'directReports') {
        sendBulkUploadFileAttachedEventDirectTeam();
      } else if (teamType === 'allReports') {
        sendBulkUploadFileAttachedEventSkipTeam();
      }
    } else if (isCompanyRoute) {
      sendBulkUploadFileAttachedEventCompany();
    }
  };

  const handleFileUploadClicked = () => {
    if (isMyDataRoute) {
      sendBulkUploadFileUploadedEventMe();
    } else if (isTeamRoute) {
      if (teamType === 'directReports') {
        sendBulkUploadFileUploadedEventDirectTeam();
      } else if (teamType === 'allReports') {
        sendBulkUploadFileUploadedEventSkipTeam();
      }
    } else if (isCompanyRoute) {
      sendBulkUploadFileUploadedEventCompany();
    }
  };

  const handleDownloadQuickGoalSampleFile = () => {
    if (isMyDataRoute) {
      sendBulkUploadDownloadQuickGoalSampleFileEventMe();
    } else if (isTeamRoute) {
      if (teamType === 'directReports') {
        sendBulkUploadDownloadQuickGoalSampleFileEventDirectTeam();
      } else if (teamType === 'allReports') {
        sendBulkUploadDownloadQuickGoalSampleFileEventSkipTeam();
      }
    } else if (isCompanyRoute) {
      sendBulkUploadDownloadQuickGoalSampleFileEventCompany();
    }
  };

  const handleDownloadExtendedGoalSampleFile = () => {
    if (isMyDataRoute) {
      sendBulkUploadDownloadExtendedGoalSampleFileEventMe();
    } else if (isTeamRoute) {
      if (teamType === 'directReports') {
        sendBulkUploadDownloadExtendedGoalSampleFileEventDirectTeam();
      } else if (teamType === 'allReports') {
        sendBulkUploadDownloadExtendedGoalSampleFileEventSkipTeam();
      }
    } else if (isCompanyRoute) {
      sendBulkUploadDownloadExtendedGoalSampleFileEventCompany();
    }
  };

  const handleViewGoalUploadGuide = () => {
    if (isMyDataRoute) {
      sendBulkUploadViewGuideEventMe();
    } else if (isTeamRoute) {
      if (teamType === 'directReports') {
        sendBulkUploadViewGuideEventDirectTeam();
      } else if (teamType === 'allReports') {
        sendBulkUploadViewGuideEventSkipTeam();
      }
    } else if (isCompanyRoute) {
      sendBulkUploadViewGuideEventCompany();
    }
  };

  if (checkIntentLandingPagesFeatureEnabled()) {
    return (
      <Layout
        Filters={
          <IntentDropdown
            breadcrumbs={{
              items: [
                {
                  label: COHORT_ID_NAME_MAP[cohort.id] || 'Me',
                  to: '',
                },
              ],
              page: {
                label: modifyString(moduleName, StringModifier.PLURAL_TITLE_CASE),
                to: '',
              },
            }}
            timelineDropdownOnChangeCallback={handleTimePeriodChange}
          />
        }
      >
        <BulkUploadScreen
          handleFileAttached={handleFileAttached}
          handleFileUploadClicked={handleFileUploadClicked}
          handleDownloadQuickGoalSampleFile={handleDownloadQuickGoalSampleFile}
          handleDownloadExtendedGoalSampleFile={handleDownloadExtendedGoalSampleFile}
          handleViewGoalUploadGuide={handleViewGoalUploadGuide}
        />
      </Layout>
    );
  }

  return (
    <>
      <Header teamName={modifiedHeaderLabel} />
      <PageTabbedNavigationV1 tabs={tabs} />
      <BulkUploadScreen
        handleFileAttached={handleFileAttached}
        handleFileUploadClicked={handleFileUploadClicked}
        handleDownloadQuickGoalSampleFile={handleDownloadQuickGoalSampleFile}
        handleDownloadExtendedGoalSampleFile={handleDownloadExtendedGoalSampleFile}
        handleViewGoalUploadGuide={handleViewGoalUploadGuide}
      />
    </>
  );
};

export { UploadGoalsIntentLandingPage };
